// The free trading journal template (Excel / Google Sheets) at /trading-journal-template.
// The files live in client/public/templates and are built with working formulas; the
// Trades tab saved as CSV imports straight into Market Matrix.
import { Link } from 'react-router-dom';
import { PublicShell, localPath, usePageLanguage, useTitle } from './PublicPage';
import { useI18n } from './i18n';
import { TEMPLATE_FILES, TEMPLATE_TABS } from './TemplateData';

const PREVIEW = [
  ['2026-10-20', 'MNQ', 'Long', '2', '21500', '21530', '+$117.20', 'Pullback', '', 'A'],
  ['2026-10-20', 'MNQ', 'Short', '2', '21560', '21572', '−$50.80', 'Reversal', 'FOMO', 'B-'],
  ['2026-10-21', 'MES', 'Long', '3', '6820.25', '6828.75', '+$123.90', 'Opening range breakout', '', 'A-'],
];

export default function TemplatePage({ user, forceLang }) {
  const lang = usePageLanguage(forceLang, '/trading-journal-template');
  const { t } = useI18n();
  useTitle(t('Free Trading Journal Template (Excel and Google Sheets) | Market Matrix'));
  const other = lang === 'es' ? 'en' : 'es';
  const startTo = user ? '/app' : '/signup';

  return (
    <PublicShell user={user} className="firm-page template-page">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Free download · no sign-up')}</p>
        <h1>{t('Free trading journal template')}</h1>
        <p className="subtitle">{t('A ready-to-use trading journal for Excel and Google Sheets, made for futures and prop firm traders. Log your trades and the P&L, win rate, profit factor and your account limits calculate themselves.')}</p>
        <div className="firm-cta">
          <a href={TEMPLATE_FILES[lang]} download className="cta-button">{t('Download the template (.xlsx)')}</a>
          <a href={TEMPLATE_FILES[other]} download className="firm-secondary">{lang === 'es' ? 'Download in English' : 'Descargar en español'}</a>
        </div>
        <p className="cta-note">{t('Free. No email needed. Works in Excel, Google Sheets and Numbers.')}</p>
      </header>

      <section className="firm-section">
        <h2>{t('What’s inside')}</h2>
        <ul className="firm-watch">
          {TEMPLATE_TABS.map(([tab, text]) => <li key={tab}><strong>{t(tab)}:</strong> {t(text)}</li>)}
        </ul>
      </section>

      <section className="firm-section">
        <h2>{t('A look at the Trades tab')}</h2>
        <div className="table-wrap">
          <table className="firm-rules template-preview">
            <thead>
              <tr>
                {['Date', 'Symbol', 'Side', 'Contracts', 'Entry price', 'Exit price', 'Net P&L', 'Setup', 'Mistake', 'Grade'].map((h) => <th key={h} scope="col">{t(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {PREVIEW.map((row) => (
                <tr key={row.join()}>
                  {row.map((cell, i) => <td key={i} className={i === 6 ? (cell.startsWith('+') ? 'gain' : 'loss') : undefined}>{i === 2 || i >= 7 ? t(cell) : cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="firm-note">{t('Example rows. Points, gross and net P&L are calculated for you from the prices, contracts and fees.')}</p>
      </section>

      <section className="firm-section">
        <h2>{t('How to use it')}</h2>
        <ol className="firm-steps">
          <li><strong>{t('Log every trade the same day.')}</strong> {t('Pick the symbol and side, enter your prices, contracts and fees, and tag the setup and any mistake.')}</li>
          <li><strong>{t('Grade the execution, not the result.')}</strong> {t('A+ to B- for how well you followed your plan. A losing A trade is fine; a winning B- trade is a warning.')}</li>
          <li><strong>{t('Review once a week.')}</strong> {t('Open the Summary tab: which setups pay, which mistakes cost the most, and your best and worst days.')}</li>
        </ol>
        <p>{t('Can’t find the tabs? The file opens on “Start here”. The other tabs (Trades, Daily review, Summary and more) are along the bottom of the window in Excel and Google Sheets, and along the top in Numbers on a Mac.')}</p>
        <p>{t('Using Google Sheets? In Google Drive choose New → File upload, then open the file with Google Sheets. The formulas work there too.')}</p>
      </section>

      <section className="firm-section">
        <h2>{t('When the spreadsheet gets slow')}</h2>
        <p>{t('Spreadsheets are a great start, but typing every trade by hand gets old, and a trailing max loss is hard to track in a formula. Market Matrix imports your TopstepX, Tradovate or NinjaTrader files directly, tracks trailing, end-of-day and intraday drawdown and the consistency rule, and shows what your habits cost you. It’s free to start.')}</p>
        <p>{t('Bring your template with you: on the Trades tab choose File → Save as (or Download) → CSV, then import that file in Market Matrix. Your trades, prices, P&L, fees and notes come in.')}</p>
        <div className="firm-cta">
          <Link to={startTo} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
          <Link to={localPath(lang, '/blog/how-to-keep-a-trading-journal')} className="firm-secondary">{t('How to keep a trading journal')}</Link>
        </div>
      </section>

      <section className="final-cta blog-final-cta">
        <h2>{t('Start logging today')}</h2>
        <a href={TEMPLATE_FILES[lang]} download className="cta-button">{t('Download the template (.xlsx)')}</a>
        <p className="firm-note">{t('This template is for journaling only and isn’t financial advice. Trading futures involves substantial risk of loss.')}</p>
      </section>
    </PublicShell>
  );
}
